import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuArrowLeft, LuBox, LuDroplet, LuGlobe, LuHammer, LuLayers, LuLeaf, LuPackage, LuRecycle, LuTrendingUp, LuWrench, LuZap } from "react-icons/lu";
import muiSytle from "../../style/components-style/muiStyle";
import "../../style/components-style/RadialOrbitalTimeline.css";

const RadialOrbitalTimeline = ({ data, building, filter = { category: "Global" } }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isDark = mode === "dark";
  // State for the Central Hub
  const [activeNodeId, setActiveNodeId] = useState(1); // Default to Total Carbon
  const [activeSubNodeId, setActiveSubNodeId] = useState(null); // For drill-down (e.g., "A1-A3")
  const [containerWidth, setContainerWidth] = useState(520);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const scale = useMemo(() => {
    const baseWidth = 520;
    if (containerWidth < baseWidth) {
      return containerWidth / baseWidth;
    }
    return 1;
  }, [containerWidth]);

  // --- DATA MAPPING ---
  const timelineData = useMemo(() => {
    if (!data) return [];

    const embodied = data.embodiedCarbon || 0;
    const operational = data.operationalCarbon || 0;
    const biogenic = data.biogenicCarbon || 0;
    const total = embodied + operational;

    // Calculate percentages
    const embodiedPct = total ? Math.round((embodied / total) * 100) : 0;
    const operationalPct = total ? Math.round((operational / total) * 100) : 0;
    const biogenicScale = total ? Math.min(Math.round((Math.abs(biogenic) / total) * 100), 100) : 0;

    const lastUpdated = new Date().toLocaleDateString();
    const area = building?.builtup_area || 1; // Correct field from DB schema
    const intensity = total / area;

    // Helper to find stage value - Improved matching logic
    const getStageValue = (stageId) => {
      const stages = data.lcaStages || data.lcaGroups || [];

      // 1. Try exact match on stage or stage_group
      let stage = stages.find((s) => s.stage === stageId || s.stage_group === stageId);

      // 2. If not found, try startsWith (e.g., "B6") matches "B6 - Operational Energy")
      if (!stage) {
        stage = stages.find((s) => (s.stage && s.stage.startsWith(stageId)) || (s.stage_group && s.stage_group.startsWith(stageId)));
      }

      // 3. Special case for B6/B7 if grouped
      if (!stage && (stageId === "B6" || stageId === "B7")) {
        const combined = stages.find((s) => s.stage_group === "B6-B7");
        if (combined) {
          // If we only have combined, estimate or show 0 if we can't split.
          // Usually B6 is dominant.
          if (stageId === "B6") return Math.abs(combined.value);
          return 0; // Fallback for B7 if not separated
        }
      }

      // 4. Handle other grouped stages (A1-A5, B1-B5, C1-C4)
      if (!stage) {
        if (stageId === "A1-A3" || stageId === "A4-A5") {
          stage = stages.find((s) => s.stage_group === "A1-A5");
        } else if (stageId === "B1-B5") {
          stage = stages.find((s) => s.stage_group === "B1-B5");
        } else if (stageId === "C1-C4") {
          stage = stages.find((s) => s.stage_group === "C1-C4");
        }
      }

      return stage ? Math.abs(stage.value) : 0;
    };

    // Helper to get icon for a stage
    const getStageIcon = (stageId) => {
      if (stageId.startsWith("A1") || stageId.startsWith("A2") || stageId.startsWith("A3")) return LuPackage;
      if (stageId.startsWith("A4") || stageId.startsWith("A5")) return LuHammer;
      if (stageId.startsWith("B1") || stageId.startsWith("B2") || stageId.startsWith("B3") || stageId.startsWith("B4") || stageId.startsWith("B5")) return LuWrench;
      if (stageId.startsWith("B6")) return LuZap;
      if (stageId.startsWith("B7")) return LuDroplet;
      if (stageId.startsWith("C1") || stageId.startsWith("C2") || stageId.startsWith("C3") || stageId.startsWith("C4")) return LuRecycle;
      return LuLayers;
    };

    // Helper to get description for a stage
    const getStageDesc = (stageId) => {
      const descriptions = {
        "A1-A3": "Product Stage (Extraction, Transport, Manufacturing)",
        "A1-A5": "Product & Construction Stage",
        "A4-A5": "Construction Process (Transport, Installation)",
        "B1-B5": "Use Stage (Maintenance, Repair, Replacement)",
        B6: "Operational Energy Use",
        "B6-B7": "Operational Energy & Water Use",
        B7: "Operational Water Use",
        "C1-C4": "End of Life (Deconstruction, Waste Processing)",
      };
      return descriptions[stageId] || stageId;
    };

    // Dynamically generate sub-nodes based on actual backend data
    const generateSubNodes = (stagePrefix) => {
      const stages = data.lcaStages || data.lcaGroups || [];
      const relevantStages = stages.filter((s) => {
        const stageId = s.stage_group || s.stage || "";
        return stageId.startsWith(stagePrefix);
      });

      return relevantStages.map((s) => {
        const stageId = s.stage_group || s.stage;
        return {
          id: stageId,
          title: stageId,
          desc: getStageDesc(stageId),
          value: Math.abs(s.value),
          icon: getStageIcon(stageId),
        };
      });
    };

    const nodes = [
      {
        id: 1,
        title: "Total Carbon",
        date: lastUpdated,
        content: `Total Global Warming Potential of the building over its lifecycle.`,
        category: "Total",
        icon: LuGlobe,
        status: "completed",
        energy: 100,
        value: total,
        intensity: intensity, // Add intensity here
        color: "#3b82f6", // Blue
        isCenter: true,
        subNodes: [], // Total doesn't have drill-down in this view
      },
      {
        id: 2,
        title: "Embodied Carbon",
        date: lastUpdated,
        content: `Carbon emissions associated with materials and construction processes.`,
        category: "Embodied",
        icon: LuBox,
        status: "completed",
        energy: embodiedPct,
        value: embodied,
        color: "#ef4444", // Red
        subNodes: generateSubNodes("A").concat(generateSubNodes("B1"), generateSubNodes("C")),
      },
      {
        id: 3,
        title: "Operational Carbon",
        date: lastUpdated,
        content: `Carbon emissions from energy and water use during operation.`,
        category: "Operational",
        icon: LuZap,
        status: "in-progress",
        energy: operationalPct,
        value: operational,
        color: "#f59e0b", // Amber
        subNodes: generateSubNodes("B6").concat(generateSubNodes("B7")),
      },
      {
        id: 4,
        title: "Biogenic Carbon",
        date: lastUpdated,
        content: `Carbon stored in bio-based materials like wood and insulation.`,
        category: "Biogenic",
        icon: LuLeaf,
        status: "completed",
        energy: biogenicScale,
        value: biogenic,
        color: "#10b981", // Emerald
        subNodes: [], // Usually single value
      },
    ];

    // Filter out Operational Carbon if category is not Global or whole_building
    if (filter.category !== "Global" && filter.category?.toLowerCase() !== "whole_building") {
      return nodes.filter((n) => n.id !== 3);
    }

    return nodes;
  }, [data, building, filter]);

  // --- ORBITAL LOGIC ---

  const handleNodeClick = (id) => {
    setActiveNodeId(id);
    setActiveSubNodeId(null); // Reset drill-down
  };

  const handleSubNodeClick = (subId) => {
    setActiveSubNodeId(subId);
  };

  const handleBackClick = (e) => {
    e.stopPropagation();
    setActiveSubNodeId(null);
  };

  const calculatePosition = (index, total) => {
    // Distribute peripheral nodes (skip center)
    const peripheralNodes = timelineData.filter((d) => !d.isCenter);
    const nodeIndex = peripheralNodes.findIndex((d) => d.id === timelineData[index].id);

    if (nodeIndex === -1) return { x: 0, y: 0 }; // Should not happen for peripheral calc

    // Fixed rotation for static view
    // Start from the top (-90 degrees) for better symmetry
    const angle = ((nodeIndex / peripheralNodes.length) * 360 - 90) % 360;
    const radius = 210;
    const radian = (angle * Math.PI) / 180;

    return {
      x: radius * Math.cos(radian),
      y: radius * Math.sin(radian),
    };
  };

  // Get Active Data
  const activeNode = timelineData.find((d) => d.id === activeNodeId) || timelineData[0];

  // Early return with loading state if no data
  if (!data || timelineData.length === 0 || !activeNode) {
    return (
      <div className="rot-container" ref={containerRef} style={{ height: 620, border: "none", display: "flex", alignItems: "center", justifyContent: "center" }} data-theme={mode}>
        <div style={{ textAlign: "center", color: isDark ? "#94a3b8" : "#64748b" }}>
          <div style={{ fontSize: "14px", marginBottom: "8px" }}>Loading timeline data...</div>
          <div className="rot-orbit-ring" style={{ width: 60, height: 60, margin: "0 auto", animation: "pulse 2s ease-in-out infinite" }}></div>
        </div>
      </div>
    );
  }

  const activeSubNode = activeSubNodeId ? activeNode.subNodes.find((s) => s.id === activeSubNodeId) : null;

  // Calculate contribution percentage for sub-nodes
  const getContribution = (val) => {
    if (!activeNode.value) return 0;
    return Math.round((val / activeNode.value) * 100);
  };

  // Visualization Logic (Progress Ring)
  const radius = 125; // Moderate progress ring size
  const circumference = 2 * Math.PI * radius;
  // If subnode active, show its contribution to parent. If parent active, show its energy (pct of total).
  const progressValue = activeSubNode ? getContribution(activeSubNode.value) : activeNode.id === 1 ? 100 : activeNode.energy;
  const strokeDashoffset = circumference - (progressValue / 100) * circumference;

  return (
    <div className="rot-container" ref={containerRef} style={{ height: 620, border: "none" }} data-theme={mode}>
      <div
        className="rot-wrapper"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          width: 520,
          height: 520,
          flexShrink: 0,
          position: "relative",
        }}>
        {/* Orbit Ring */}
        <div className="rot-orbit-ring rot-ring-1"></div>

        {/* CENTRAL HUB */}
        <div className="rot-central-hub" style={{ borderColor: activeNode.color + "20" }}>
          {/* SVG Progress Ring */}
          <svg className="rot-hub-svg" width="270" height="270" viewBox="0 0 270 270" style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
            <circle cx="135" cy="135" r={radius} stroke={isDark ? "#27272a" : "#f1f5f9"} strokeWidth="2" fill="none" />
            <circle
              cx="135"
              cy="135"
              r={radius}
              stroke={activeNode.color}
              strokeWidth="3"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              transform="rotate(-90 135 135)"
              style={{ transition: "stroke-dashoffset 0.5s ease" }}
            />
          </svg>

          <div className="rot-hub-content">
            {activeSubNode ? (
              // DRILL-DOWN VIEW
              <div className="rot-hub-drilldown fade-in">
                {/* Back Button - Repositioned to top-left */}
                <button className="rot-back-btn" onClick={handleBackClick} style={{ top: "30px", left: "30px", transform: "none" }}>
                  <LuArrowLeft /> Back
                </button>

                <Typography
                  sx={{
                    ...muiSytle.typographyChartTitle,
                    fontSize: "14px",
                    textTransform: "uppercase",
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 600,
                    letterSpacing: "1px",
                    marginTop: "40px",
                  }}>
                  {activeSubNode.title}
                </Typography>

                <Typography sx={{ fontFamily: "Poppins", fontSize: "42px", fontWeight: 700, color: activeNode.color, lineHeight: 1.1 }}>
                  {Math.round(activeSubNode.value).toLocaleString()}
                </Typography>

                <Typography sx={{ fontFamily: "Poppins", fontSize: "16px", color: isDark ? "#a1a1aa" : "#94a3b8", fontWeight: 500 }}>kgCO₂e</Typography>

                {/* Extra Visualization: Contribution Bar */}
                <Box sx={{ width: "60%", marginTop: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                    <span style={{ fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b" }}>Contribution to {activeNode.category}</span>
                    <span style={{ fontSize: "11px", fontWeight: 600, color: activeNode.color }}>{getContribution(activeSubNode.value)}%</span>
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={getContribution(activeSubNode.value)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#f1f5f9",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: activeNode.color,
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              </div>
            ) : (
              // MAIN NODE VIEW
              <div className="rot-hub-main fade-in">
                <Typography
                  sx={{
                    ...muiSytle.typographyChartTitle,
                    fontSize: "14px",
                    textTransform: "uppercase",
                    color: isDark ? "#94a3b8" : "#64748b",
                    fontWeight: 600,
                    letterSpacing: "1px",
                  }}>
                  {activeNode.category}
                </Typography>

                <Typography sx={{ fontFamily: "Poppins", fontSize: "42px", fontWeight: 700, color: activeNode.color, lineHeight: 1.1 }}>
                  {Math.round(activeNode.value).toLocaleString()}
                </Typography>

                <Typography sx={{ fontFamily: "Poppins", fontSize: "16px", color: isDark ? "#a1a1aa" : "#94a3b8", fontWeight: 500 }}>kgCO₂e</Typography>

                {/* Extra Feature: Carbon Intensity for Total Carbon */}
                {activeNode.id === 1 && activeNode.intensity && (
                  <div className="rot-hub-meta" style={{ marginTop: "8px" }}>
                    <span className="rot-meta-item">
                      <LuTrendingUp className="meta-icon" /> {Math.round(activeNode.intensity)} kgCO₂e/m²
                    </span>
                  </div>
                )}

                {/* Sub-node Chips with Visualization */}
                {activeNode.subNodes && activeNode.subNodes.length > 0 && (
                  <div className="rot-hub-chips">
                    {activeNode.subNodes.map((sub) => {
                      const pct = getContribution(sub.value);
                      return (
                        <button
                          key={sub.id}
                          className="rot-hub-chip"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubNodeClick(sub.id);
                          }}
                          style={{
                            borderColor: activeNode.color + "50",
                            position: "relative",
                            overflow: "hidden",
                            color: isDark ? "#f1f5f9" : "#475569",
                          }}>
                          {/* Background Bar Visualization */}
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: `${pct}%`,
                              background: activeNode.color,
                              opacity: 0.1,
                              zIndex: 0,
                            }}
                          />
                          <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: "6px" }}>
                            {sub.icon && <sub.icon className="chip-icon" />} {sub.title}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* PERIPHERAL NODES */}
        {timelineData.map((item, index) => {
          if (item.isCenter) return null; // Don't render center node as an orbital

          const position = calculatePosition(index, timelineData.length);
          const isActive = activeNodeId === item.id;
          const Icon = item.icon;

          return (
            <div
              key={item.id}
              className={`rot-node ${isActive ? "active" : ""}`}
              style={{
                transform: `translate(${position.x}px, ${position.y}px)`,
                zIndex: 20,
                opacity: 1, // Always fully visible as requested
              }}
              onClick={() => handleNodeClick(item.id)}
              title={item.title}>
              {/* Pulse Effect for active node */}
              {isActive && (
                <div
                  className="rot-pulse-ring"
                  style={{
                    borderColor: item.color,
                    boxShadow: `0 0 15px ${item.color}40`,
                  }}
                />
              )}

              {/* Node Circle */}
              <div
                className="rot-node-circle"
                style={{
                  borderColor: isActive ? item.color : isDark ? "#3f3f46" : "#e2e8f0",
                  background: isActive ? item.color : isDark ? "#27272a" : "white",
                  color: isActive ? "white" : isDark ? "#a1a1aa" : "#64748b",
                  transform: isActive ? "scale(1.2)" : "scale(1)",
                }}>
                <Icon className="rot-node-icon" />
              </div>

              {/* Label & Value Visualization */}
              <div
                className="rot-node-label"
                style={{
                  opacity: isActive ? 1 : 0.7,
                  fontWeight: isActive ? 600 : 400,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                }}>
                <span>{item.title}</span>
                {/* Always show value for better visualization */}
                <span style={{ fontSize: "15px", color: item.color, fontWeight: 700 }}>{Math.round(item.value).toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RadialOrbitalTimeline;
